import { ResolvedPersonLiteDto } from '../api/network/models/getNetworkScopedDtos'
import { SharedWorkHistoryLiteDto } from '../api/role/models/SharedWorkHistoryDto'

export type TransformedResolvedPerson = {
    personId: string
    companies: TransformedCompany[]
}

export type TransformedCompany = {
    companyId: string
    connections: TransformedConnection[]
}

export type TransformedConnection = {
    seedPersonId: string
    resolvedPersonJobTitles: string[]
    seedPersonJobTitles: string[]
    cumulativeOverlapMs: number
    overallStart: Date
    overallEnd: Date
}

export function transformNetworkScopedRes(
    resolvedPersonLites: ResolvedPersonLiteDto[],
): TransformedResolvedPerson[] {
    const now = new Date()

    return resolvedPersonLites.map((resolvedPerson) => {
        const companyMap = new Map<
            string,
            Map<string, SharedWorkHistoryLiteDto[]>
        >()

        for (const connection of resolvedPerson.connectionDetails) {
            const seedPersonId = connection.seedPersonId

            for (const swh of connection.sharedWorkHistory) {
                const { companyId } = swh

                const companyEntry =
                    companyMap.get(companyId) ??
                    new Map<string, SharedWorkHistoryLiteDto[]>()
                const existing = companyEntry.get(seedPersonId) ?? []

                existing.push(swh)
                companyEntry.set(seedPersonId, existing)
                companyMap.set(companyId, companyEntry)
            }
        }

        const companies: TransformedCompany[] = Array.from(
            companyMap.entries(),
        ).map(([companyId, seedMap]) => {
            const connections: TransformedConnection[] = Array.from(
                seedMap.entries(),
            ).map(([seedPersonId, history]) => {
                const resolvedTitles: string[] = []
                const seedTitles: string[] = []
                const overlaps: { start: Date; end: Date }[] = []

                for (const swh of history) {
                    const {
                        resolvedPersonWorkedAt,
                        seedPersonWorkedAt,
                        startOfOverlap,
                        endOfOverlap,
                    } = swh

                    if (
                        !resolvedPersonWorkedAt.startDate ||
                        !seedPersonWorkedAt.startDate
                    ) {
                        continue
                    }

                    resolvedTitles.push(resolvedPersonWorkedAt.jobTitle)
                    seedTitles.push(seedPersonWorkedAt.jobTitle)

                    const start = new Date(startOfOverlap ?? now)
                    const end = new Date(endOfOverlap ?? now)

                    overlaps.push({ start, end })
                }

                const uniqueResolved = [...new Set(resolvedTitles)]
                const uniqueSeed = [...new Set(seedTitles)]

                let cumulativeOverlapMs = 0
                let earliestStart = now
                let latestEnd = new Date(0)

                for (const { start, end } of overlaps) {
                    cumulativeOverlapMs += end.getTime() - start.getTime()
                    if (start < earliestStart) earliestStart = start
                    if (end > latestEnd) latestEnd = end
                }

                return {
                    seedPersonId,
                    resolvedPersonJobTitles: uniqueResolved,
                    seedPersonJobTitles: uniqueSeed,
                    cumulativeOverlapMs,
                    overallStart: earliestStart,
                    overallEnd: latestEnd,
                }
            })

            connections.sort((a, b) => {
                const aEnd = a.overallEnd?.getTime() ?? 0
                const bEnd = b.overallEnd?.getTime() ?? 0
                return bEnd - aEnd
            })

            return {
                companyId,
                connections,
            }
        })

        companies.sort((a, b) => {
            const aLatest = Math.max(
                ...a.connections.map((c) => c.overallEnd.getTime()),
            )
            const bLatest = Math.max(
                ...b.connections.map((c) => c.overallEnd.getTime()),
            )
            return bLatest - aLatest
        })

        return {
            personId: resolvedPerson.personId,
            companies,
        }
    })
}
